import { call, put, takeLatest } from 'redux-saga/effects';
import { AdditionalPageOptions } from 'app/interface';
import { getCMSConfig } from '../../util/cms';
import { initApp, initOverlay, setLoading, setOverlayLoaded, setTokenState, toggleOverlay } from './action';
import { AppAction } from './enum';
import { requestHtml } from '@vfde-sails/utils';
import { trackLeadManagement } from '../../tracking/tracking';
import { LeadManagementTrigger } from '../../tracking/enum';
import { debugInfo } from '../../util/debug';
import { seamlessCheckSaga } from '../AddressValidation/saga';
import { TokenState } from '../../enum';

import { getFFToken, postValidateToken } from './generator';
import { setFFCookie } from '../../util/cookie';
import { ValidateTokenResponse } from './interface';

export function* initOverlaySaga({ overlay }: ReturnType<typeof initOverlay>) {
    try {
        const { leadManagement }: AdditionalPageOptions = yield call(getCMSConfig);

        if (!leadManagement.formUrl || !overlay) {
            throw new Error('leadManagementUrl config not found');
        }

        const content: string = yield call(requestHtml, leadManagement.formUrl);

        const contextAndFunction = {
            context: overlay,
            fn: overlay.injectContent as (...content: string[]) => void,
        };

        yield call(contextAndFunction, content);
        yield put(setOverlayLoaded(true));
    } catch (error) {
        yield call(debugInfo, error);
    }
}

export function* toggleOverlaySaga({ shouldOpen }: ReturnType<typeof toggleOverlay>) {
    if (!shouldOpen) {
        return;
    }

    yield call(trackLeadManagement, LeadManagementTrigger.REVEAL);
}

export function* validateTokenSaga () {
    try {
        yield put(setLoading(true));

        const ffToken: string | null = yield call(getFFToken);

        if (!ffToken) {
            return;
        }

        const response: ValidateTokenResponse = yield call(postValidateToken, ffToken);

        if (!response || response.ffv2.state !== TokenState.VALID) {
            return;
        }

        yield put(setTokenState(response.ffv2.state));
        yield call(setFFCookie, response.ffv2.token);
    }
    catch (error) {
        yield call(debugInfo, error);
    }
    finally {
        yield put(setLoading(false));
    }
}

export function* initAppSaga({ overlay }: ReturnType<typeof initApp>) {
    yield call(validateTokenSaga);
    yield put(initOverlay(overlay));
    yield call(seamlessCheckSaga);
}

export default function* appSaga() {
    yield takeLatest(AppAction.INIT_OVERLAY, initOverlaySaga);
    yield takeLatest(AppAction.TOGGLE_OVERLAY, toggleOverlaySaga);
    yield takeLatest(AppAction.INIT_APP, initAppSaga);
}
